// apps/accounts/src/pages/staff/StaffAttendance.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Calendar, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

export default function StaffAttendance() {
  const { user } = useAuth();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchToday();
  }, []);

  const fetchToday = async () => {
    try {
      const date = new Date().toISOString().split('T')[0];
      const data = await pb.collection('staff_attendance')
        .getFirstListItem(`staff="${user.id}" && date="${date}"`)
        .catch(() => null);
      setRecord(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const markStatus = async (status) => {
    try {
      const date = new Date().toISOString().split('T')[0];
      const existing = await pb.collection('staff_attendance')
        .getFirstListItem(`staff="${user.id}" && date="${date}"`)
        .catch(() => null);

      if (existing) {
        await pb.collection('staff_attendance').update(existing.id, { status });
      } else {
        await pb.collection('staff_attendance').create({
          staff: user.id,
          date: date,
          status,
        });
      }
      toast.success(`Marked as ${status}`);
      fetchToday();
    } catch (err) {
      toast.error('Failed: ' + err.message);
    }
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;

  const currentStatus = record?.status || 'present'; // Default is Present
  const isLeave = record?.leave_request; // If linked to a leave request

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">My Attendance</h1>
        <span className="text-sm text-slate-400">
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </span>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* --- Status Display ----------------------------------- */}
            <div>
              <p className="text-sm text-slate-500 font-medium">Today's Status</p>
              <div className="flex items-center gap-2 mt-1">
                {currentStatus === 'present' && (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <p className="text-2xl font-bold text-green-600">Present</p>
                  </>
                )}
                {currentStatus === 'absent' && (
                  <>
                    <XCircle className="w-6 h-6 text-red-500" />
                    <p className="text-2xl font-bold text-red-600">
                      {isLeave ? 'On Leave (Absent)' : 'Absent'}
                    </p>
                  </>
                )}
              </div>
              {isLeave && (
                <p className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded mt-1">
                  ?? Leave approved � you are marked absent for today.
                </p>
              )}
              <p className="text-xs text-slate-400 mt-1">
                {record ? `Last updated: ${new Date(record.updated).toLocaleTimeString()}` : 'Not marked yet'}
              </p>
            </div>

            {/* --- Action Buttons ---------------------------------- */}
            <div className="flex flex-wrap gap-2">
              {/* Show Mark Present button if not already present */}
              {currentStatus !== 'present' && !isLeave && (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => markStatus('present')}
                >
                  <CheckCircle className="w-4 h-4 mr-1" /> Mark Present
                </Button>
              )}

              {/* Show Mark Absent button if not already absent */}
              {currentStatus !== 'absent' && !isLeave && (
                <Button
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  onClick={() => markStatus('absent')}
                >
                  <XCircle className="w-4 h-4 mr-1" /> Mark Absent
                </Button>
              )}

              {/* If on Leave, disable buttons */}
              {isLeave && (
                <span className="text-sm text-slate-500 bg-slate-100 px-3 py-2 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Action restricted
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}